// liveTail — combine a room's R2 stroke-log timeline with the un-flushed
// live tail of the WS server's session buffer (絵ログ live tail —
// growth-timelapse-share-2026-07-02.md §4-§5, feature/stroke-log-live-tail).
//
// Why this exists: R2 stroke-log segments are only flushed on snapshot
// publish (~5 min cadence), so the newest ~5 minutes of drawing lives ONLY
// in the WS server's in-memory session buffer. The server exposes it over
// the `whiteboard.stroke-log-tail-*` WS round-trip (protocol
// `events/strokeLogTail.ts`). The client asks for everything past the
// highest seq it already has from R2 (`sinceSeq = max(coverage.toSeq)`),
// wraps the response into a synthetic `StrokeLogSegment` tagged
// `flushReason: 'live'`, and re-merges it with the R2 segments through the
// existing `mergeSegments` pipeline — the live tail's `flushedAtMs`
// (server `now`) is strictly larger than any R2 segment's, so it WINS every
// overlapping range and any shared seq is de-duplicated (larger-flushedAtMs
// -wins, range-based; see segmentMerge.ts header).
//
// The WS transport only exists on the main thread (`SessionProvider` owns
// the socket, workers cannot reach it), so these helpers run main-side; the
// combined event list is then handed to the worker/generation verbatim via
// `TimelapseEncodeRequest.preloadedTimeline` (encodeJob.ts) instead of the
// worker re-fetching. Everything here is pure (no fetch / no transport) so
// it is unit-testable in the node env alongside segmentMerge.

import {
  STROKE_LOG_SEGMENT_VERSION,
  type StrokeLogSegment,
  type WhiteboardStrokeLogTail,
} from '@oekaki/protocol';
import { mergeSegments, type SeqRange } from './segmentMerge.js';
import type { StrokeLogTimeline } from './strokeLogTimeline.js';

/**
 * The `sinceSeq` to ask the WS server for = the highest seq the client
 * already has from R2 (`max(coverage.toSeq)`). The server returns only
 * events with `seq > sinceSeq`. `0` when there is no R2 coverage yet (a
 * fresh room whose drawing has not been snapshotted → the whole session
 * buffer is the tail).
 */
export const liveTailSinceSeq = (coverage: readonly SeqRange[]): number => {
  let max = 0;
  for (const r of coverage) if (r.toSeq > max) max = r.toSeq;
  return max;
};

/**
 * Wrap a `whiteboard.stroke-log-tail` WS response into a synthetic
 * `StrokeLogSegment` so it can flow through `mergeSegments` alongside the
 * downloaded R2 segments. Returns `null` for an empty tail (`toSeq <=
 * sinceSeq` or no events) — nothing new to add, so the caller skips it and
 * uses the R2 timeline unchanged.
 *
 * `flushReason: 'live'` marks it as never-persisted (it exists only in this
 * client's memory); replay semantics are identical to a durable segment.
 */
export const buildLiveTailSegment = (
  roomId: string,
  tail: WhiteboardStrokeLogTail,
): StrokeLogSegment | null => {
  if (tail.toSeq <= tail.sinceSeq || tail.events.length === 0) return null;
  return {
    version: STROKE_LOG_SEGMENT_VERSION,
    roomId,
    fromSeq: tail.sinceSeq + 1,
    toSeq: tail.toSeq,
    flushedAtMs: tail.flushedAtMs,
    flushReason: 'live',
    events: tail.events,
  };
};

/**
 * Fold the live-tail segment into an already-merged R2 timeline.
 *
 * The R2 timeline's `coverage` ranges are rebuilt into low-priority
 * synthetic segments (each carrying the merged events that fall inside it,
 * `flushedAtMs: 0` so the live tail always wins) and re-merged together
 * with the live segment. This is the `mergeSegments` re-run the backend
 * hand-off describes ("R2 セグメント配列に足して既存 mergeSegments に渡す
 * だけ") expressed on the merged output, so coverage / gaps are recomputed
 * consistently and any seq the live tail shares with R2 is de-duplicated
 * (the live tail wins). `skippedSegments` (a download-time diagnostic) is
 * preserved from the R2 pass.
 *
 * A `null` live segment (empty tail / non-member / WS failure) returns the
 * R2 timeline unchanged — the whole feature degrades to "R2 only".
 */
export const combineLiveTail = (
  timeline: StrokeLogTimeline,
  liveSegment: StrokeLogSegment | null,
): StrokeLogTimeline => {
  if (liveSegment === null) return timeline;
  const r2Segments: StrokeLogSegment[] = timeline.coverage.map((range) => ({
    version: STROKE_LOG_SEGMENT_VERSION,
    roomId: liveSegment.roomId,
    fromSeq: range.fromSeq,
    toSeq: range.toSeq,
    // Always loses overlap resolution to the live tail (server-now). The R2
    // segments are already overlap-resolved into `timeline.events`, so ties
    // among these disjoint coverage ranges never matter.
    flushedAtMs: 0,
    flushReason: 'snapshot',
    events: timeline.events.filter((ev) => ev.seq >= range.fromSeq && ev.seq <= range.toSeq),
  }));
  const merged = mergeSegments([...r2Segments, liveSegment]);
  return { ...merged, skippedSegments: timeline.skippedSegments };
};
